/**
 * Analytics — M7 flagship insights page.
 *
 * A date-range picker (From/To + 7d/30d/90d presets) feeds every section:
 * overview tiles, lead funnel, sources, response times, consultant
 * leaderboard, appointments, AI contribution and campaign attribution.
 *
 * Every backend report degrades to {available:false} when its source table
 * is missing — each section renders "No data yet" for that case. MOCK_*
 * fallbacks keep the page rendering without a backend.
 */
import { useMemo, useState } from 'react';
import { formatInOrgTz } from '../lib/datetime';
import TopBar from '../components/TopBar';
import { analyticsService } from '../lib/services';
import { useApi } from '../lib/hooks';

const PRESETS = [
  { id: '7d', label: '7d', days: 7 },
  { id: '30d', label: '30d', days: 30 },
  { id: '90d', label: '90d', days: 90 },
];

const STAGE_LABELS = {
  INBOUND: 'Inbound',
  CONTACTED: 'Contacted',
  APPOINTMENT: 'Qualified',
  NEGOTIATION: 'Negotiation',
  CONFIRMED: 'Quoted',
  DELIVERY_BOOKED: 'Booked',
  RETAILED: 'Retailed',
  BOOKED: 'Booked',
  DELIVERED: 'Delivered',
};

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('en-AU'));
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const fmtMinutes = (v) => {
  if (v == null) return '—';
  const m = Number(v);
  if (m < 1) return `${Math.round(m * 60)}s`;
  if (m < 90) return `${m.toFixed(1)}m`;
  return `${(m / 60).toFixed(1)}h`;
};
const dateInput = (d) => d.toISOString().slice(0, 10);

/* ── Mock fallbacks (one per section) ── */
const MOCK_OVERVIEW = {
  available: true, totalLeads: 342, openLeads: 118, appointmentsToday: 9,
  showRatePct: 71.4, firstContactP90Minutes: 12.6, winRatePct: 18.7, aiAssistedSharePct: 43.9,
};
const MOCK_FUNNEL = {
  available: true, totalLeads: 342, lostLeads: 61,
  stages: [
    { stage: 'INBOUND', count: 58, reached: 342, conversionFromPreviousPct: null },
    { stage: 'CONTACTED', count: 98, reached: 284, conversionFromPreviousPct: 83.0 },
    { stage: 'APPOINTMENT', count: 39, reached: 186, conversionFromPreviousPct: 65.5 },
    { stage: 'TEST_DRIVE', count: 33, reached: 158, conversionFromPreviousPct: 84.9 },
    { stage: 'NEGOTIATION', count: 63, reached: 147, conversionFromPreviousPct: 93.0 },
    { stage: 'CONFIRMED', count: 21, reached: 105, conversionFromPreviousPct: 71.4 },
    { stage: 'DELIVERY_BOOKED', count: 6, reached: 84, conversionFromPreviousPct: 80.0 },
    { stage: 'RETAILED', count: 14, reached: 78, conversionFromPreviousPct: 92.9 },
    { stage: 'DELIVERED', count: 64, reached: 64, conversionFromPreviousPct: 76.2 },
  ],
};
const MOCK_SOURCES = {
  available: true,
  sources: [
    { source: 'CARSALES', leads: 121, appointments: 44, testDrives: 31, won: 26, winRatePct: 21.5 },
    { source: 'WEB_FORM', leads: 84, appointments: 29, testDrives: 18, won: 14, winRatePct: 16.7 },
    { source: 'PHONE', leads: 63, appointments: 21, testDrives: 12, won: 11, winRatePct: 17.5 },
    { source: 'WALK_IN', leads: 41, appointments: 18, testDrives: 14, won: 9, winRatePct: 22.0 },
    { source: 'FACEBOOK_MARKETPLACE', leads: 33, appointments: 7, testDrives: 4, won: 4, winRatePct: 12.1 },
  ],
};
const MOCK_RESPONSE_TIMES = {
  available: true, leadsCreated: 342, leadsMeasured: 318,
  p50Minutes: 2.4, p90Minutes: 12.6, p95Minutes: 31.2, within90SecondsPct: 38.7,
};
const MOCK_LEADERBOARD = {
  available: true,
  consultants: [
    { consultantId: 'u1', name: 'Mia Torres', leads: 74, appointments: 31, testDrives: 22, won: 17, tasksCompleted: 96, winRatePct: 23.0 },
    { consultantId: 'u2', name: 'Jack Nguyen', leads: 68, appointments: 24, testDrives: 18, won: 12, tasksCompleted: 71, winRatePct: 17.6 },
    { consultantId: 'u3', name: 'Ella Whitmore', leads: 55, appointments: 21, testDrives: 15, won: 11, tasksCompleted: 84, winRatePct: 20.0 },
    { consultantId: 'u4', name: 'Sam Okafor', leads: 49, appointments: 15, testDrives: 9, won: 7, tasksCompleted: 52, winRatePct: 14.3 },
  ],
};
const MOCK_APPOINTMENTS = {
  available: true, total: 128,
  byStatus: { SCHEDULED: 22, CONFIRMED: 18, CHECKED_IN: 14, COMPLETED: 55, NO_SHOW: 11, CANCELLED: 8 },
  byType: { TEST_DRIVE: 61, SALES_CONSULT: 34, SERVICE: 24, DELIVERY: 9 },
  bySource: { AI: 47, HUMAN: 81 },
  showRatePct: 57.5, noShowRatePct: 9.2,
};
const MOCK_AI = {
  available: true, aiLeads: 150, humanLeads: 192, aiWon: 31, humanWon: 33,
  aiLeadSharePct: 43.9, aiWinRatePct: 20.7, humanWinRatePct: 17.2, aiAppointmentSharePct: 36.7,
};
const MOCK_ATTRIBUTION = {
  available: true,
  campaigns: [
    { campaignId: 'c1', name: 'EOFY Runout Blast', status: 'RUNNING', sends: 900, sent: 842, blocked: 21, attributedLeads: 17 },
    { campaignId: 'c2', name: 'Service Due Nurture', status: 'RUNNING', sends: 412, sent: 388, blocked: 9, attributedLeads: 11 },
    { campaignId: 'c3', name: 'Birthday Wishes', status: 'COMPLETED', sends: 130, sent: 118, blocked: 4, attributedLeads: 3 },
  ],
};

/* ── Section card wrapper: handles the {available:false} degrade ── */
function Section({ title, sub, data, children }) {
  const unavailable = data && data.available === false;
  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="flex-between" style={{ marginBottom: 10 }}>
        <span className="card-label" style={{ margin: 0 }}>{title}</span>
        {sub && <span className="mono text-xs text-muted">{sub}</span>}
      </div>
      {unavailable ? (
        <div className="empty-state" style={{ padding: '20px 8px', textAlign: 'center' }}>
          <div className="text-muted">No data yet</div>
        </div>
      ) : children}
    </div>
  );
}

function StatTile({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div className="mono text-xs text-muted" style={{ letterSpacing: 0.4 }}>{label}</div>
      <div className="metric-value" style={{ fontSize: 24, color: color || 'var(--text-primary)', marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

/* Simple key→count mini table for the appointment breakdowns. */
function MiniTable({ title, map }) {
  const entries = Object.entries(map || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div>
      <div className="card-label" style={{ marginBottom: 6 }}>{title}</div>
      {entries.length === 0 && <div className="text-muted text-xs">No data</div>}
      {entries.map(([k, v]) => (
        <div key={k} className="flex-between" style={{ padding: '3px 0' }}>
          <span className="mono text-xs text-muted">{k}</span>
          <span className="mono text-xs" style={{ fontWeight: 600 }}>{fmtNum(v)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Analytics() {
  const [fromDate, setFromDate] = useState(() => dateInput(new Date(Date.now() - 30 * 86400000)));
  const [toDate, setToDate] = useState(() => dateInput(new Date()));
  const [activePreset, setActivePreset] = useState('30d');

  /* ISO instants for the API — from at 00:00, to at 23:59:59 local. */
  const range = useMemo(() => ({
    from: new Date(`${fromDate}T00:00:00`).toISOString(),
    to: new Date(`${toDate}T23:59:59`).toISOString(),
  }), [fromDate, toDate]);
  const deps = [range.from, range.to];
  const applyPreset = (id, days) => {
    setActivePreset(id);
    setFromDate(dateInput(new Date(Date.now() - days * 86400000)));
    setToDate(dateInput(new Date()));
  };

  const { data: overview } = useApi(() => analyticsService.overview(range), { devFallback: MOCK_OVERVIEW, deps });
  const { data: funnel } = useApi(() => analyticsService.funnel(range), { devFallback: MOCK_FUNNEL, deps });
  const { data: sources } = useApi(() => analyticsService.sources(range), { devFallback: MOCK_SOURCES, deps });
  const { data: responseTimes } = useApi(() => analyticsService.responseTimes(range), { devFallback: MOCK_RESPONSE_TIMES, deps });
  const { data: leaderboard } = useApi(() => analyticsService.leaderboard(range), { devFallback: MOCK_LEADERBOARD, deps });
  const { data: appointments } = useApi(() => analyticsService.appointments(range), { devFallback: MOCK_APPOINTMENTS, deps });
  const { data: ai } = useApi(() => analyticsService.aiContribution(range), { devFallback: MOCK_AI, deps });
  const { data: attribution } = useApi(() => analyticsService.campaignAttribution(range), { devFallback: MOCK_ATTRIBUTION, deps });

  const rangeLabel = `${formatInOrgTz(range.from, { day: '2-digit', month: '2-digit', year: 'numeric' })} → ${formatInOrgTz(range.to, { day: '2-digit', month: '2-digit', year: 'numeric' })}`;

  /* Funnel bar scale */
  const funnelMax = Math.max(...(funnel?.stages || []).map((s) => s.reached), 1);

  /* AI split bar scale */
  const aiTotal = (ai?.aiLeads || 0) + (ai?.humanLeads || 0);

  return (
    <>
      <TopBar
        crumbs={['Analytics']}
        right={
          <div
            className="analytics-topbar-actions"
            style={{ display: 'flex', gap: 8, alignItems: 'center', flexwrap: 'wrap', rowGap: 8 }}>
            <input
              type="date" className="input" value={fromDate} max={toDate}
              onChange={(e) => { setActivePreset(null); setFromDate(e.target.value); }}
              style={{ fontSize: 12, padding: '4px 8px' }}
              aria-label="From date"
            />
            <span className="mono text-xs text-muted">→</span>
            <input
              type="date" className="input" value={toDate} min={fromDate}
              onChange={(e) => { setActivePreset(null); setToDate(e.target.value); }}
              style={{ fontSize: 12, padding: '4px 8px' }}
              aria-label="To date"
            />
            <div className="period-toggle" role="tablist" aria-label="Range presets">
              {PRESETS.map((p) => (
                <button
                  key={p.id} role="tab"
                  aria-selected={activePreset === p.id}
                  className={activePreset === p.id ? 'active' : ''}
                  onClick={() => applyPreset(p.id, p.days)}
                >{p.label}</button>
              ))}
            </div>
          </div>
        }
      />
      <div className="app-content">
        <div className="page-header">
          <div>
            <div className="page-header-title">Analytics</div>
            <div className="page-header-sub">Funnel, sources, response times and leaderboards · {rangeLabel}</div>
          </div>
        </div>

        {/* (a) Overview */}
        <Section title="OVERVIEW" sub={rangeLabel} data={overview}>
          <div className="grid-7">
            <StatTile label="TOTAL LEADS" value={fmtNum(overview?.totalLeads)} color="var(--accent-primary)" />
            <StatTile label="OPEN" value={fmtNum(overview?.openLeads)} color="var(--info)" />
            <StatTile label="APPTS TODAY" value={fmtNum(overview?.appointmentsToday)} color="var(--accent-primary)" />
            <StatTile label="SHOW RATE" value={fmtPct(overview?.showRatePct)} color="var(--success)" />
            <StatTile label="1ST CONTACT P90" value={fmtMinutes(overview?.firstContactP90Minutes)} color="var(--warning)" />
            <StatTile label="WIN RATE" value={fmtPct(overview?.winRatePct)} color="var(--success)" />
            <StatTile label="AI-ASSISTED" value={fmtPct(overview?.aiAssistedSharePct)} color="var(--info)" />
          </div>
        </Section>

        {/* (b) Funnel */}
        <Section
          title="LEAD FUNNEL"
          sub={`${fmtNum(funnel?.totalLeads)} leads · ${fmtNum(funnel?.lostLeads)} lost`}
          data={funnel}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(funnel?.stages || []).map((s) => (
              <div key={s.stage} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="mono text-xs text-muted" style={{ width: 130, flexShrink: 0 }}>
                  {STAGE_LABELS[s.stage] || s.stage}
                </span>
                <div style={{ flex: 1, background: 'var(--bg-glass)', borderRadius: 4, height: 16 }}>
                  <div style={{
                    width: `${(100 * s.reached) / funnelMax}%`, height: '100%',
                    borderRadius: 4, background: 'var(--accent-primary)', opacity: 0.85,
                    minWidth: s.reached > 0 ? 2 : 0,
                  }} />
                </div>
                <span className="mono text-xs" style={{ width: 60, textAlign: 'right', fontWeight: 600 }}>
                  {fmtNum(s.reached)}
                </span>
                <span className="mono text-xs text-muted" style={{ width: 64, textAlign: 'right' }}>
                  {s.conversionFromPreviousPct == null ? '' : `${fmtPct(s.conversionFromPreviousPct)} ▸`}
                </span>
              </div>
            ))}
            {(funnel?.stages || []).length === 0 && (
              <div className="text-muted text-xs">No stage data for this window.</div>
            )}
          </div>
        </Section>

        <div className="grid-2">
          {/* (c) Sources */}
          <Section title="SOURCES" data={sources}>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>{['Source', 'Leads', 'Appts', 'Test drives', 'Won', 'Win rate'].map((h) => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {(sources?.sources || []).length === 0 && (
                    <tr><td colSpan={6} className="text-muted" style={{ padding: 12, textAlign: 'center' }}>No source data.</td></tr>
                  )}
                  {(sources?.sources || []).map((s) => (
                    <tr key={s.source}>
                      <td className="mono text-xs">{s.source}</td>
                      <td className="mono text-xs">{fmtNum(s.leads)}</td>
                      <td className="mono text-xs">{fmtNum(s.appointments)}</td>
                      <td className="mono text-xs">{fmtNum(s.testDrives)}</td>
                      <td className="mono text-xs">{fmtNum(s.won)}</td>
                      <td className="mono text-xs" style={{ color: 'var(--success)', fontWeight: 600 }}>
                        {fmtPct(s.winRatePct)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* (d) Response times */}
          <Section
            title="RESPONSE TIMES"
            sub={`${fmtNum(responseTimes?.leadsMeasured)} of ${fmtNum(responseTimes?.leadsCreated)} leads measured`}
            data={responseTimes}
          >
            <div className="grid-3" style={{ marginBottom: 14 }}>
              <StatTile label="P50" value={fmtMinutes(responseTimes?.p50Minutes)} color="var(--success)" />
              <StatTile label="P90" value={fmtMinutes(responseTimes?.p90Minutes)} color="var(--warning)" />
              <StatTile label="P95" value={fmtMinutes(responseTimes?.p95Minutes)} color="var(--danger)" />
            </div>
            <div style={{ textAlign: 'center', paddingTop: 8, borderTop: '1px solid var(--border-color)' }}>
              <div className="metric-value" style={{ fontSize: 38, color: 'var(--accent-primary)' }}>
                {fmtPct(responseTimes?.within90SecondsPct)}
              </div>
              <div className="mono text-xs text-muted">FIRST CONTACT WITHIN 90 SECONDS</div>
            </div>
          </Section>
        </div>

        {/* (e) Leaderboard */}
        <Section title="CONSULTANT LEADERBOARD" data={leaderboard}>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>{['Consultant', 'Leads', 'Appts', 'Test drives', 'Won', 'Tasks done', 'Win rate'].map((h) => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {(leaderboard?.consultants || []).length === 0 && (
                  <tr><td colSpan={7} className="text-muted" style={{ padding: 12, textAlign: 'center' }}>No consultant activity.</td></tr>
                )}
                {(leaderboard?.consultants || []).map((c) => (
                  <tr key={c.consultantId}>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td className="mono text-xs">{fmtNum(c.leads)}</td>
                    <td className="mono text-xs">{fmtNum(c.appointments)}</td>
                    <td className="mono text-xs">{fmtNum(c.testDrives)}</td>
                    <td className="mono text-xs">{fmtNum(c.won)}</td>
                    <td className="mono text-xs">{fmtNum(c.tasksCompleted)}</td>
                    <td className="mono text-xs" style={{ color: 'var(--success)', fontWeight: 600 }}>
                      {fmtPct(c.winRatePct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <div className="grid-2">
          {/* (f) Appointments */}
          <Section title="APPOINTMENTS" sub={`${fmtNum(appointments?.total)} total`} data={appointments}>
            <div className="grid-2" style={{ marginBottom: 14 }}>
              <StatTile label="SHOW RATE" value={fmtPct(appointments?.showRatePct)} color="var(--success)" />
              <StatTile label="NO-SHOW RATE" value={fmtPct(appointments?.noShowRatePct)} color="var(--danger)" />
            </div>
            <div className="grid-2">
              <MiniTable title="BY TYPE" map={appointments?.byType} />
              <MiniTable title="BY SOURCE" map={appointments?.bySource} />
            </div>
          </Section>

          {/* (g) AI contribution */}
          <Section title="AI CONTRIBUTION" data={ai}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <div className="flex-between mono text-xs text-muted" style={{ marginBottom: 4 }}>
                  <span>LEADS · AI {fmtNum(ai?.aiLeads)} vs HUMAN {fmtNum(ai?.humanLeads)}</span>
                  <span>{fmtPct(ai?.aiLeadSharePct)} AI share</span>
                </div>
                <div style={{
                  display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden',
                  background: 'var(--bg-glass)', border: '1px solid var(--border-color)'
                }}>
                  <div style={{
                    width: `${aiTotal ? (100 * (ai?.aiLeads || 0)) / aiTotal : 0}%`,
                    background: 'var(--info)'
                  }} title={`AI: ${fmtNum(ai?.aiLeads)}`} />
                  <div style={{
                    width: `${aiTotal ? (100 * (ai?.humanLeads || 0)) / aiTotal : 0}%`,
                    background: 'var(--accent-primary)'
                  }} title={`Human: ${fmtNum(ai?.humanLeads)}`} />
                </div>
              </div>
              <div className="grid-2">
                <StatTile label="AI WIN RATE" value={fmtPct(ai?.aiWinRatePct)} color="var(--info)" />
                <StatTile label="HUMAN WIN RATE" value={fmtPct(ai?.humanWinRatePct)} color="var(--accent-primary)" />
              </div>
              <div className="flex-between mono text-xs text-muted"
                style={{ paddingTop: 8, borderTop: '1px solid var(--border-color)' }}>
                <span>AI won {fmtNum(ai?.aiWon)} · Human won {fmtNum(ai?.humanWon)}</span>
                <span>AI booked {fmtPct(ai?.aiAppointmentSharePct)} of appts</span>
              </div>
            </div>
          </Section>
        </div>

        {/* (h) Campaign attribution */}
        <Section title="CAMPAIGN ATTRIBUTION" sub="last-touch · 14-day lookback" data={attribution}>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>{['Campaign', 'Status', 'Sends', 'Sent', 'Blocked', 'Attributed leads'].map((h) => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {(attribution?.campaigns || []).length === 0 && (
                  <tr><td colSpan={6} className="text-muted" style={{ padding: 12, textAlign: 'center' }}>No campaign sends in this window.</td></tr>
                )}
                {(attribution?.campaigns || []).map((c) => (
                  <tr key={c.campaignId}>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td className="mono text-xs">{c.status}</td>
                    <td className="mono text-xs">{fmtNum(c.sends)}</td>
                    <td className="mono text-xs" style={{ color: 'var(--success)' }}>{fmtNum(c.sent)}</td>
                    <td className="mono text-xs" style={{ color: 'var(--warning)' }}>{fmtNum(c.blocked)}</td>
                    <td className="mono text-xs" style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>
                      {fmtNum(c.attributedLeads)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </>
  );
}
